import * as _ from 'lodash';
import { useMemo, useState, type VoidFunctionComponent } from 'react';
import Helmet from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';

import { Action, ResourceType } from '../../../../typings/permissions';
import { useOnConfirm } from '../../../contexts/DialogContext';
import { useDresser } from '../../../contexts/DrawerContext';
import { useParams } from '../../../contexts/RouterContext';
import { window } from '../../../globals';
import useLicenses from '../../../hooks/api/licenses/useLicenses';
import useCreatePolicyRules from '../../../hooks/api/policies/useCreatePolicyRules';
import useDeletePolicyRules from '../../../hooks/api/policies/useDeletePolicyRules';
import { useFetchPolicy } from '../../../hooks/api/policies/useFetchPolicy';
import useUpdatePolicyRules from '../../../hooks/api/policies/useUpdatePolicyRules';
import useIsLicenseConcludedEnabled from '../../../hooks/features/useIsLicenseConcludedEnabled';
import PolicyModule from '../../../redux/policies';
import useHasPermission from '../../../shared/permissions/useHasPermission';
import { hasError, verify_subscription } from '../../../utils';
import Button from '../../generic/Button/Button';
import { ErrorAlert } from '../../generic/ErrorAlert';
import SearchInput from '../../generic/Forms/SearchInput';
import { icons } from '../../generic/Icon/icons';
import Loader from '../../generic/Loader';
import LicenseDrawer from '../../licenses/LicenseDrawer';
import { customizeableLicensePoliciesFeature } from '../../settings/billing/features/consts';
import EditPolicyCTA from '../EditPolicyCTA';
import { RuleItem } from '../rulesRevamp/RuleItem';
import RuleList from '../rulesRevamp/RuleList';
import { RuleModal } from '../rulesRevamp/RuleModal';
import type { LicensingRuleType, PolicyAction } from '../../../../shared/model-typings';
import type { LicenseResponse } from '../../../hooks/api/licenses/useLicense';
import type { AsyncStatus, LicensingRule, Policy } from '../../../types';
import type { NewRule, RuleModalMode } from '../rulesRevamp/RuleModal';

import { LicensePolicyPageSubnav } from './LicensePolicySubnav';
import usePolicyState from './usePolicyData';

import styles from './LicensePolicyPage.module.less';

export type LicenseIdOnly = {
  licenseId: string;
};

const DisplayLicensePolicyPageRevamp: VoidFunctionComponent = () => {
  const params = useParams<{ policyId: number }>();

  const [shouldShowRuleModal, setShowRuleModal] = useState<boolean>(false);
  const [modalMode, setModalMode] = useState<Partial<RuleModalMode>>({ editType: 'CREATE', type: 'approved_license' });

  const [unifiedSearch, setUnifiedSearch] = useState<string>('');

  const {
    selectedUncategorized,
    setSelectedUncategorized,
    selectedDenied,
    setSelectedDenied,
    selectedFlagged,
    setSelectedFlagged,
    selectedApproved,
    setSelectedApproved,
    deselectAll,
  } = usePolicyState();

  const onConfirm = useOnConfirm();

  const organizationId = window.user?.organization?.id;
  const isLicenseConcludedEnabled = useIsLicenseConcludedEnabled(organizationId as number);

  const dispatch = useDispatch();
  const status: AsyncStatus = useSelector(PolicyModule.selectors.getStatus);

  // dispatches
  const updatePolicy = PolicyModule.actions.update as (
    policy: Partial<Policy> & { defaultAction: PolicyAction },
  ) => void;

  const { data: policy, isLoading: isLoadingPolicy, refetch: refetchPolicy } = useFetchPolicy(params.policyId);

  const { data: licenses, isLoading: isLoadingLicenses } = useLicenses(false, { pageSize: 4999 });

  const { isLoading: isDeleting, mutate: bulkDelete } = useDeletePolicyRules();
  const { mutate: updatePolicyRules, isLoading: isUpdatingPolicyRules } = useUpdatePolicyRules();
  const { mutate: createPolicyRules, isLoading: isCreatingPolicyRules } = useCreatePolicyRules();

  const { pushDrawer, popDrawer } = useDresser();
  const hasPermission = useHasPermission();

  const licenseNamesMap = useMemo(() => {
    return licenses?.reduce(
      (acc, license) => {
        acc[license.licenseId] = license.name;
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [licenses]);

  if (!policy || isLoadingLicenses || isDeleting || isLoadingPolicy || isUpdatingPolicyRules || isCreatingPolicyRules) {
    return <Loader />;
  }

  const hideRuleModal = () => setShowRuleModal(false);

  //  Permission check (for the buttons)
  const canUserEditLicensingPolicies = hasPermission({
    action: Action.Edit,
    resourceType: ResourceType.LicensingPolicyAny,
  });

  const onEdit = (licenseId: string) => {
    onBulkEdit(new Set([licenseId]));
    popDrawer();
  };
  const onDelete = (licenseId: string) => {
    onBulkDelete(new Set([licenseId]));
    popDrawer();
  };

  const handleDelete = (licenseId: string, ruleId: number) =>
    onConfirm({
      title: 'Delete Rule',
      body: `Are you sure you want to delete ${licenseId}?`,
      onConfirm: () => ruleId && onDelete(licenseId),
      confirmText: 'Delete',
      confirmBtnType: 'error',
    });

  const showLicenseDetails = (licenseId: string | null, ruleId: number | null) => {
    const buttons =
      canUserEditLicensingPolicies && ruleId && licenseId
        ? [
          <Button
            key="edit"
            size="medium"
            type="secondary"
            icon={icons.edit}
            isDisabled={!licenseId || !canUserEditLicensingPolicies}
            onClick={() => onEdit(licenseId)}
          >
            Edit Rule
          </Button>,
          <Button
            key="delete"
            size="medium"
            type="error"
            icon={icons.trashRegular}
            onClick={() => handleDelete(licenseId, ruleId)}
          >
            Delete Rule
          </Button>,
          ]
        : [];

    pushDrawer(<LicenseDrawer scope="global" licenseId={licenseId ?? ''} buttons={buttons} />, {
      shouldSkipClickOutside: true,
      onClose: () => popDrawer(),
    });
  };

  const onCheckRule = (isChecked: boolean, licenseId: string, ruleType?: LicensingRuleType) => {
    if (ruleType === 'denied_license') {
      if (isChecked) {
        setSelectedDenied(new Set([...selectedDenied, licenseId]));
      } else {
        selectedDenied.delete(licenseId);
        setSelectedDenied(new Set(selectedDenied));
      }
    } else if (ruleType === 'flagged_license') {
      if (isChecked) {
        setSelectedFlagged(new Set([...selectedFlagged, licenseId]));
      } else {
        selectedFlagged.delete(licenseId);
        setSelectedFlagged(new Set(selectedFlagged));
      }
    } else if (ruleType === 'approved_license') {
      if (isChecked) {
        setSelectedApproved(new Set([...selectedApproved, licenseId]));
      } else {
        selectedApproved.delete(licenseId);
        setSelectedApproved(new Set(selectedApproved));
      }
    } else if (isChecked) {
      setSelectedUncategorized(new Set([...selectedUncategorized, licenseId]));
    } else {
      selectedUncategorized.delete(licenseId);
      setSelectedUncategorized(new Set(selectedUncategorized));
    }
  };

  // Bulk create is the action for moving licenses from uncategorized to denied, flagged, or approved
  const onBulkCreate = (selectedLicenses: Set<string>) => {
    if (verify_subscription('premium')) {
      setModalMode({
        type: 'denied_license',
        editType: 'CREATE',
        rules: [...selectedLicenses].map((l) => ({ licenseId: l })) as LicensingRule[],
      });
      setShowRuleModal(true);
    } else {
      window.showPlanSelectorModal({ focusFeature: customizeableLicensePoliciesFeature });
    }
  };

  // Bulk edit is the action for moving licenses from denied, flagged, or approved to any other category
  // or editing the rule details in place.
  const onBulkEdit = (selectedLicenses: Set<string>) => {
    if (verify_subscription('premium')) {
      const allRules = (policy?.rules as LicensingRule[]) || [];
      const rules = _.compact(allRules.filter((rule) => selectedLicenses.has(rule.licenseId)));
      setModalMode({
        rules,
        editType: 'EDIT',
      });

      setShowRuleModal(true);
    } else {
      window.showPlanSelectorModal({ focusFeature: customizeableLicensePoliciesFeature });
    }
  };

  // Bulk delete is the action for moving licenses from any category back to the uncategorized list.
  const onBulkDelete = (selectedLicenses: Set<string>) => {
    if (verify_subscription('premium')) {
      const rulesInList = (policy?.rules as LicensingRule[]) || [];
      const rules = _.compact(rulesInList.filter((rule) => selectedLicenses.has(rule.licenseId)));
      bulkDelete({ policyId: policy.id, ruleIds: rules.map((rule) => rule.ruleId) });
      deselectAll();
    } else {
      window.showPlanSelectorModal({ focusFeature: customizeableLicensePoliciesFeature });
    }
  };

  // Create rule is the action for adding a single new rule to any one category.
  const createRule = (type: LicensingRuleType) => {
    if (verify_subscription('premium')) {
      setModalMode({ type, editType: 'CREATE', rules: [] });
      setShowRuleModal(true);
    } else {
      window.showPlanSelectorModal({ focusFeature: customizeableLicensePoliciesFeature });
    }
  };

  // Submit is the action for submitting the rule modal, which can be for editing or creating a rule.
  const submit = (newRules: Array<{ rule: NewRule; editedRuleId?: number }>) => {
    if (modalMode.editType === 'EDIT') {
      const payload = newRules.map((rule) => ({
        editedRuleId: rule.editedRuleId as number,
        editedRule: rule.rule,
      }));
      updatePolicyRules({ policyId: policy.id, rules: payload });
    }
    if (modalMode.editType === 'CREATE') {
      const payload = newRules.map((rule) => rule.rule);
      createPolicyRules({ policyId: policy.id, newRules: { newRules: payload } });
    }
    deselectAll();
    hideRuleModal();
  };

  // onChangeDefault is the action for changing the default column for the policy.
  const onChangeDefault = (defaultAction: PolicyAction) => {
    dispatch(updatePolicy({ id: policy.id, defaultAction }));
    refetchPolicy();
  };

  const searchLicense = (license: LicenseResponse) => {
    return license.name?.toLowerCase().includes(unifiedSearch.toLowerCase()) ||
      license.licenseId.toLowerCase().includes(unifiedSearch.toLowerCase());
  };

  const uncategorizedLicenses: LicenseResponse[] = licenses ? licenses
    .filter((license) => !policy.rules?.some((rule) => (rule as LicensingRule).licenseId === license.licenseId))
    .filter((license) => searchLicense(license))
    .sort((a, b) => a.licenseId.toLowerCase().localeCompare(b.licenseId.toLowerCase())) : [];

  return (
    <>
      <EditPolicyCTA policy={policy} status={status} />
      {isLicenseConcludedEnabled && (
        <LicensePolicyPageSubnav policyId={params.policyId} activeKey="rules" />
      )}
      <div className="container-fluid margin-top">
        <Helmet title={status.loading ? 'Policy...' : `${policy.title} - Policies`} />
        {status.loading ? (
          (<Loader />)
        ) : (
          <>
            {hasError(status) && <ErrorAlert status={status} />}
            <div className={styles.ruleSearch}>
              <SearchInput
                placeholder="Search by license name"
                label="license search"
                onChange={(e) => setUnifiedSearch(e)}
                value={unifiedSearch}
              />
            </div>
            <div className={styles.columnContainer}>
              {/* See: https://github.com/fossas/FOSSA/pull/2613 for comments on uncategorized licenses */}
              {/* Uncategorized Licenses */}
              <RuleList
                // Uncategorized should never be the default
                isDefault={false}
                policy={policy}
                title="Uncategorized"
                icon={icons.circleQuestionSolid}
                uncategorizedLicenses={uncategorizedLicenses.filter((l) => searchLicense(l))}
                selectedLicenses={selectedUncategorized}
                setSelectedLicenses={setSelectedUncategorized}
                onBulkAction={() => onBulkCreate(selectedUncategorized)}
                onCheck={onCheckRule}
                onBulkDelete={() => onBulkDelete(selectedUncategorized)}
                isDeleting={isDeleting}
                onLicenseClick={showLicenseDetails}
                searchLicense={searchLicense}
                licenseNamesMap={licenseNamesMap}
              >
                {isLoadingLicenses || !licenses ? (
                  <Loader />
                ) : (
                  uncategorizedLicenses
                    .filter((l) => searchLicense(l))
                    .map((l) => (
                      <RuleItem
                        key={l.licenseId}
                        rule={l as unknown as LicensingRule}
                        onCheck={onCheckRule}
                        isChecked={selectedUncategorized.has(l.licenseId)}
                        onLicenseClick={showLicenseDetails}
                        licenseName={l.name}
                      />
                    ))
                )}
              </RuleList>
              {/* Denied Licenses */}
              <RuleList
                isDefault={policy.latestVersion?.defaultAction === 'DENY'}
                onRuleAdd={() => createRule('denied_license')}
                onSetAsDefault={() => onChangeDefault('DENY')}
                policy={policy}
                search={unifiedSearch}
                selectedLicenses={selectedDenied}
                setSelectedLicenses={setSelectedDenied}
                onCheck={onCheckRule}
                onBulkAction={() => onBulkEdit(selectedDenied)}
                isDeleteEnabled
                title="Deny"
                icon={icons.xCircleSolid}
                ruleType="denied_license"
                onBulkDelete={() => onBulkDelete(selectedDenied)}
                isDeleting={isDeleting}
                onLicenseClick={showLicenseDetails}
                searchLicense={searchLicense}
                licenseNamesMap={licenseNamesMap}
              />
              {/* Flagged Licenses */}
              <RuleList
                isDefault={policy.latestVersion?.defaultAction === 'FLAG'}
                onRuleAdd={() => createRule('flagged_license')}
                onSetAsDefault={() => onChangeDefault('FLAG')}
                search={unifiedSearch}
                onCheck={onCheckRule}
                selectedLicenses={selectedFlagged}
                setSelectedLicenses={setSelectedFlagged}
                onBulkAction={() => onBulkEdit(selectedFlagged)}
                isDeleteEnabled
                title="Flag for Review"
                icon={icons.flag}
                ruleType="flagged_license"
                onBulkDelete={() => onBulkDelete(selectedFlagged)}
                isDeleting={isDeleting}
                policy={policy}
                onLicenseClick={showLicenseDetails}
                searchLicense={searchLicense}
                licenseNamesMap={licenseNamesMap}
              />
              {/* Approved Licenses */}
              <RuleList
                isDefault={policy.latestVersion?.defaultAction === 'APPROVE'}
                onRuleAdd={() => createRule('approved_license')}
                onSetAsDefault={() => onChangeDefault('APPROVE')}
                search={unifiedSearch}
                selectedLicenses={selectedApproved}
                setSelectedLicenses={setSelectedApproved}
                onBulkAction={() => onBulkEdit(selectedApproved)}
                onCheck={onCheckRule}
                isDeleteEnabled
                title="Approve"
                icon={icons.checkCircle}
                ruleType="approved_license"
                policy={policy}
                onBulkDelete={() => onBulkDelete(selectedApproved)}
                isDeleting={isDeleting}
                onLicenseClick={showLicenseDetails}
                searchLicense={searchLicense}
                licenseNamesMap={licenseNamesMap}
              />
            </div>
          </>
        )}
        <RuleModal
          show={shouldShowRuleModal}
          mode={modalMode as RuleModalMode}
          onClose={hideRuleModal}
          onSubmit={submit}
        />
      </div>
    </>
  );
};

export default DisplayLicensePolicyPageRevamp;
